import React, { useState } from "react";
import FolderGrid from "./FolderGrid";
import FolderDetail from "./FolderDetail";

const FALLBACK_T = {
  upgradeButton: "Upgrade Plan",
  vaultSummary: "Vault Summary",
  categories: "Categories",
  categoriesSub: "Folders in your vault",
  files: "Files",
  filesSub: "Files securely stored",
  legacyContacts: "Legacy Contacts",
  legacyContactsSub: "People with vault access",
  myVault: "My Vault",
  clickCategoryHint: "Click a category to view its files.",
  noFilesYet: "No files yet. Add a folder to get started.",
  addFolder: "Add Folder",
  accessOverview: "Legacy Contact Access Overview",
  fullAccess: "✓ Full Access",
  partialAccess: "◑ Partial Access",
  continuityCheckIn: "Continuity Check-In",
  checkedInSuccessfully: "Checked-in Successfully!",
  nextCheckIn: "Your next check-in is in 90 days.",
  footerNote:
    "If you don't check in before your next deadline, your vault unlocks automatically — the legacy contacts you've chosen get access to the documents you've shared with them.",
};

export default function VaultPage({
  files,
  nominees = [],
  token,
  plan = { name: "Basic", maxFilesPerFolder: 5, unlimited: false, noPlanSelected: false },
  onUpgrade,
  user,
  onUploadComplete,
  extraCategories = [],
  onAddFolder,
  t = FALLBACK_T,
  folderCardT,
}) {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const displayName = user?.name || user?.username || user?.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const fileCategories = [...new Set(files.map((f) => f.category || "Personal"))];
  const categories = [...new Set([...fileCategories, ...extraCategories])];

  const now = new Date();
  const checkinStr =
    now.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " · " +
    now.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

  // Build a map: category → nominees who can see it
  // A "full access" nominee can see every folder.
  // A "partial" nominee can only see folders in their allowedFolders list.
  const nomineesByCategory = {};
  categories.forEach((cat) => {
    nomineesByCategory[cat] = nominees.filter(
      (n) =>
        n.accessLevel === "full" ||
        (n.accessLevel === "partial" &&
          Array.isArray(n.allowedFolders) &&
          n.allowedFolders.includes(cat))
    );
  });

  // ── Folder detail drill-down ──────────────────────────────────────────────
  if (selectedCategory) {
    return (
      <FolderDetail
        category={selectedCategory}
        files={files}
        token={token}
        plan={plan}
        onBack={() => setSelectedCategory(null)}
        onUploadComplete={onUploadComplete}
        nominees={nominees}
      />
    );
  }

  // ── Vault dashboard ───────────────────────────────────────────────────────
  return (
    <div className="px-8 py-10">

      {/* Greeting — avatar + current plan tier */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center font-bold text-white text-lg flex-shrink-0">
            {initials}
          </div>
          <div className={`inline-flex items-center gap-2 text-xs font-medium rounded-full px-3 py-1.5 border ${
            plan.noPlanSelected
              ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
              : "text-gray-400 bg-dark-card border-dark-border"
          }`}>
            {plan.noPlanSelected ? (
              "No plan selected — uploads locked"
            ) : (
              <>
                <span className="text-primary">{plan.name} Plan</span>
                <span className="text-gray-600">·</span>
                {plan.unlimited ? "Unlimited files per folder" : `${plan.maxFilesPerFolder} files per folder`}
              </>
            )}
          </div>
        </div>
        {!plan.unlimited && (
          <button
            onClick={onUpgrade}
            className="text-xs font-semibold text-primary hover:text-primary-dark transition"
          >
            {plan.noPlanSelected ? "Choose a Plan" : t.upgradeButton}
          </button>
        )}
      </div>

      {/* Vault Summary */}
      <div className="bg-dark-card border border-dark-border rounded-2xl p-6 mb-6 hover:border-primary transition">
        <h2 className="text-lg font-bold mb-4">{t.vaultSummary}</h2>
        <div className="grid grid-cols-3 gap-4">
          {/* Categories */}
          <div className="bg-gradient-to-br from-primary to-primary-dark rounded-xl p-5 text-white">
            <div className="text-4xl font-bold mb-1">
              {String(categories.length || 0).padStart(2, "0")}
            </div>
            <div className="text-sm opacity-80">{t.categories}</div>
            <div className="text-xs opacity-60 mt-0.5">{t.categoriesSub}</div>
          </div>

          {/* Files */}
          <div className="bg-gradient-to-br from-primary to-primary-dark rounded-xl p-5 text-white">
            <div className="text-4xl font-bold mb-1">
              {String(files.length).padStart(2, "0")}
            </div>
            <div className="text-sm opacity-80">{t.files}</div>
            <div className="text-xs opacity-60 mt-0.5">{t.filesSub}</div>
          </div>

          {/* Nominees */}
          <div className="bg-gradient-to-br from-primary to-primary-dark rounded-xl p-5 text-white">
            <div className="text-4xl font-bold mb-1">
              {String(nominees.length).padStart(2, "0")}
            </div>
            <div className="text-sm opacity-80">{t.legacyContacts}</div>
            <div className="text-xs opacity-60 mt-0.5">{t.legacyContactsSub}</div>
          </div>
        </div>
      </div>

      {/* My Vault folder grid */}
      <div className="bg-dark-card border border-dark-border rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold">{t.myVault}</h2>
        </div>
        <p className="text-xs text-white mb-5">{t.clickCategoryHint}</p>

        {files.length === 0 && categories.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-5xl mb-3">🗄️</div>
            <p className="text-gray-400 text-sm">{t.noFilesYet}</p>
          </div>
        ) : (
          <FolderGrid
            files={files}
            categories={categories}
            onFolderClick={setSelectedCategory}
            nomineesByCategory={nomineesByCategory}
            t={folderCardT}
          />
        )}

        {onAddFolder && (
          <button
            onClick={onAddFolder}
            className="w-full flex items-center justify-center gap-2 mt-5 py-3 rounded-xl border-2 border-dashed border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary/10 text-primary font-semibold text-sm transition"
          >
            <span className="text-lg leading-none">＋</span>
            {t.addFolder}
          </button>
        )}
      </div>

      {/* Nominee Access Overview — only show when nominees exist */}
      {nominees.length > 0 && (
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6 mb-6 hover:border-primary transition">
          <h2 className="text-lg font-bold mb-4">{t.accessOverview}</h2>
          <div className="space-y-3">
            {nominees.map((n) => (
              <div
                key={n.id}
                className="flex items-center gap-3 bg-dark-bg border border-dark-border rounded-xl px-4 py-3"
              >
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
                  {n.name[0].toUpperCase()}
                </div>

                {/* Name + scope */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-white">{n.name}</div>
                  <div className="text-xs text-gray-400 truncate">{n.email}</div>
                  {n.accessLevel === "partial" && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(n.allowedFolders || []).map((f) => (
                        <span
                          key={f}
                          className="text-xs px-2 py-0.5 bg-blue-900 bg-opacity-40 text-blue-300 border border-blue-800 rounded-full"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Badge */}
                <span
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold flex-shrink-0 ${
                    n.accessLevel === "full"
                      ? "bg-emerald-500 bg-opacity-20 text-emerald-400 border border-emerald-500 border-opacity-30"
                      : "bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-30"
                  }`}
                >
                  {n.accessLevel === "full" ? t.fullAccess : t.partialAccess}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continuity Check-In status */}
      <div className="bg-dark-card border border-dark-border rounded-2xl p-6 hover:border-primary transition">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold mb-1">{t.continuityCheckIn}</h2>
            <p className="text-sm text-emerald-400 font-medium mb-1">{t.checkedInSuccessfully}</p>
            <p className="text-xs text-gray-500">{checkinStr}</p>
            <p className="text-xs text-gray-400 mt-1">{t.nextCheckIn}</p>
          </div>
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg flex-shrink-0">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>
        </div>
        <p className="text-xs text-white leading-relaxed border-t border-dark-border mt-4 pt-3">
          {t.footerNote}
        </p>
      </div>
    </div>
  );
}
